import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from 'react';
import { Button, Card, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from "react-redux";
import TextFieldAtom from "../../../../components/atoms/text-field/text-field";
import {
    projectPlanningApi,
    useUpdateProjectPlanningMutation,
} from "../../../../store/services/projectPlanningService";
import { formatCurrencyToLocale, formatValueToLocale } from "../../../../utils/method";
import './PlanningHierarchy.scss';

function PlanningHierarchy({
    hierarchy,
    totals,
    projectId,
    projectVat,
    canEdit = false,
    canCreate = false,
    canDelete = false,
}) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [updatePlanning] = useUpdateProjectPlanningMutation();
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [budgetValues, setBudgetValues] = useState({});

    // Initialize budget values from hierarchy data
    React.useEffect(() => {
        if (hierarchy?.length) {
            const initialBudgetValues = {};
            hierarchy?.forEach(category => {
                if (category.items) {
                    category.items.forEach(item => {
                        // Format to 2 decimal places
                        const budgetValue = item.forecastedBudget !== null && item.forecastedBudget !== undefined
                            ? parseFloat(item.forecastedBudget).toFixed(2)
                            : '';
                        initialBudgetValues[item.id] = budgetValue;
                    });
                }
            });
            setBudgetValues(initialBudgetValues);
        }
    }, [hierarchy]);

    const toggleGroup = (groupId) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupId)) {
            newExpanded.delete(groupId);
        } else {
            newExpanded.add(groupId);
        }
        setExpandedGroups(newExpanded);
    };

    const handleBudgetChange = (itemId, value) => {
        setBudgetValues(prev => ({
            ...prev,
            [itemId]: value
        }));
    };

    const handleBudgetBlur = async (itemId) => {
        const budgetValue = parseFloat(budgetValues[itemId]) || 0;

        // Format to 2 decimal places for display
        setBudgetValues(prev => ({
            ...prev,
            [itemId]: budgetValue.toFixed(2)
        }));

        if (projectId) {
            const patch = dispatch(
                projectPlanningApi.util.updateQueryData(
                    "getProjectPlanning",
                    projectId,
                    (draft) => {
                        draft?.hierarchy?.forEach((category) => {
                            category.items?.forEach((item) => {
                                if (item.id === itemId) {
                                    const oldBudget = item.forecastedBudget || 0;
                                    item.forecastedBudget = budgetValue;
                                    if (category.totals) {
                                        category.totals.forecastedBudget +=
                                            budgetValue - oldBudget;
                                    }
                                }
                            });
                        });
                    },
                ),
            );

            const planningData = [{
                id: itemId,
                forecastedBudget: budgetValue,
            }];

            try {
                await updatePlanning({ projectId, planningData }).unwrap();
            } catch {
                patch.undo();
            }
        }
    };

    const expandAll = () => {
        const allGroupIds = new Set();
        if (hierarchy) {
            hierarchy.forEach((category) => {
                allGroupIds.add(category.id);
            });
        }
        setExpandedGroups(allGroupIds);
    };

    const collapseAll = () => {
        setExpandedGroups(new Set());
    };

    const renderCategoryGroup = (category) => {
        const categoryId = category.id;
        const isExpanded = expandedGroups.has(categoryId);

        // Use pre-calculated totals from backend
        const totalNettoForecast = category.totals.forecastedBudget || 0;
        const totalBruttoForecast = category.totals.forecastedBudgetGross || 0;
        const totalNettoWithRisk = category.totals.forecastedBudgetWithRisk || 0;
        const totalBruttoWithRisk = category.totals.forecastedBudgetWithRiskGross || 0;
        const differenceNetto = category.totals.differenceCostCalculationNet || 0;
        const differenceBrutto = category.totals.differenceCostCalculationGross || 0;
        const differencePercent = category.totals.differenceCostCalculationPercent || 0;

        return (
            <>
                <tr
                    key={categoryId}
                    className="planning-category-header"
                    onClick={() => toggleGroup(categoryId)}
                >
                    <td className="planning-col--12">
                        <div className="d-flex align-items-center gap-2">
                            <FontAwesomeIcon
                                icon={isExpanded ? faChevronDown : faChevronRight}
                                className="ph-chevron"
                            />
                            <span>∑ {category.label}</span>
                        </div>
                    </td>
                    <td className="planning-col--15">{category.name}</td>
                    <td className="text-end planning-col--12">
                        {formatCurrencyToLocale(totalNettoForecast)}
                    </td>
                    <td className="text-end planning-col--11">
                        {formatCurrencyToLocale(totalBruttoForecast)}
                    </td>
                    <td className="text-end planning-col--12">
                        {formatCurrencyToLocale(totalNettoWithRisk)}
                    </td>
                    <td className="text-end planning-col--11">
                        {formatCurrencyToLocale(totalBruttoWithRisk)}
                    </td>
                    <td className="text-end planning-col--9">
                        <span className={differenceNetto >= 0 ? 'text-success' : 'text-danger'}>
                            {formatCurrencyToLocale(differenceNetto)}
                        </span>
                    </td>
                    <td className="text-end planning-col--9">
                        <span className={differenceBrutto >= 0 ? 'text-success' : 'text-danger'}>
                            {formatCurrencyToLocale(differenceBrutto)}
                        </span>
                    </td>
                    <td className="text-end planning-col--9">
                        <span className={differencePercent <= 100 ? 'text-success' : 'text-danger'}>
                            {formatValueToLocale(differencePercent)}%
                        </span>
                    </td>
                </tr>

                {isExpanded && category.items.map(item => (
                    <React.Fragment key={item.id}>
                        {renderItemRow(item)}
                    </React.Fragment>
                ))}
            </>
        );
    };

    const renderItemRow = (item) => {
        // Use current budget value from state (may be edited)
        const nettoForecast = parseFloat(budgetValues[item.id]) || 0;
        const vatMultiplier = 1 + (projectVat / 100);

        // If budget was edited locally, recalculate. Otherwise use pre-calculated values from backend
        const budgetChanged = nettoForecast !== (item.forecastedBudget || 0);

        const bruttoForecast = budgetChanged
            ? nettoForecast * vatMultiplier
            : (item.forecastedBudgetGross || 0);

        const nettoWithRisk = item.forecastedBudgetWithRisk || 0;
        const bruttoWithRisk = item.forecastedBudgetWithRiskGross || 0;

        // Recalculate differences if budget changed, otherwise use backend values
        // Difference = Budget - Forecast (positive = under budget, negative = over budget)
        const differenceNetto = budgetChanged
            ? nettoForecast - nettoWithRisk
            : (item.differenceNet || 0);
        const differenceBrutto = budgetChanged
            ? differenceNetto * vatMultiplier
            : (item.differenceGross || 0);
        // Percentage shows budget utilization (>100% = over budget)
        // Special case: if budget is 0 but forecast > 0, show as significantly over budget
        let differencePercent;
        if (budgetChanged) {
            if (nettoForecast === 0 && nettoWithRisk > 0) {
                differencePercent = 999; // Indicate severe over-budget
            } else if (nettoForecast !== 0) {
                differencePercent = (nettoWithRisk / nettoForecast) * 100;
            } else {
                differencePercent = 0;
            }
        } else {
            differencePercent = item.differencePercent || 0;
        }

        return (
            <tr className="planning-item-row">
                <td className="planning-col--12 ph-item-indent">
                    <span className="din-code">{item.code}</span>
                </td>
                <td className="planning-col--15">
                    <span>{item.name}</span>
                    {item.notes && (
                        <div className="text-muted small mt-1">
                            <em>{item.notes}</em>
                        </div>
                    )}
                </td>
                {/* Kostenberechnung - Netto (Editable) */}
                <td className="text-end planning-col--12">
                    <TextFieldAtom
                        type="number"
                        size="sm"
                        value={budgetValues[item.id] || ''}
                        onChange={(e) => handleBudgetChange(item.id, e.target.value)}
                        onBlur={() => handleBudgetBlur(item.id)}
                        placeholder="0.00"
                        step="0.01"
                        className="text-end ph-input-sm"
                        suffix="€"
                        disabled={!canEdit}
                        readOnly={!canEdit}
                    />
                </td>
                {/* Kostenberechnung - Brutto (Calculated) */}
                <td className="text-end planning-col--11">
                    <span>
                        {formatCurrencyToLocale(bruttoForecast)}
                    </span>
                </td>
                {/* Prognose Bauende inkl. Risiko - Netto */}
                <td className="text-end planning-col--12">
                    <span >
                        {formatCurrencyToLocale(nettoWithRisk)}
                    </span>
                </td>
                {/* Prognose Bauende inkl. Risiko - Brutto */}
                <td className="text-end planning-col--11">
                    <span>
                        {formatCurrencyToLocale(bruttoWithRisk)}
                    </span>
                </td>
                {/* Differenz - Netto (Budget - Forecast: positive = under budget = green) */}
                <td className="text-end planning-col--9">
                    <span className={differenceNetto >= 0 ? 'text-success' : 'text-danger'}>
                        {formatCurrencyToLocale(differenceNetto)}
                    </span>
                </td>
                {/* Differenz - Brutto */}
                <td className="text-end planning-col--9">
                    <span className={differenceBrutto >= 0 ? 'text-success' : 'text-danger'}>
                        {formatCurrencyToLocale(differenceBrutto)}
                    </span>
                </td>
                {/* Differenz - Percent (Budget utilization: >100% = over budget = red) */}
                <td className="text-end planning-col--9">
                    <span className={differencePercent <= 100 ? 'text-success' : 'text-danger'}>
                        {formatValueToLocale(differencePercent)}%
                    </span>
                </td>
            </tr>
        );
    };

    const hierarchyData = hierarchy || [];

    if (!hierarchy?.length) {
        return (
            <Card className="shadow-sm border-0">
                <Card.Body className="text-center py-5">
                    <p className="text-muted mb-0">
                        {t("planning.no-data")}
                    </p>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className="planning-hierarchy-card">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 header-title">
                        {t("planning.budget-planning")}
                    </h5>
                    <div className="action-buttons">
                        <Button
                            size="sm"
                            onClick={expandAll}
                            className="btn-expand"
                        >
                            <FontAwesomeIcon icon={faChevronDown} />
                            {t("common.expand-all")}
                        </Button>
                        <Button
                            size="sm"
                            onClick={collapseAll}
                            className="btn-collapse"
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                            {t("common.collapse-all")}
                        </Button>
                    </div>
                </div>
            </Card.Header>
            <Card.Body className="card-body">
                <div className="table-container">
                    <Table className="planning-table">
                        <thead className="table-dark">
                            {/* Main Header Row */}
                            <tr>
                                <th rowSpan={2} className="planning-col--12 text-uppercase fw-semibold align-middle border-end border-secondary">
                                    {t('planning.din-276-code')}
                                </th>
                                <th rowSpan={2} className="planning-col--15 text-uppercase fw-semibold align-middle border-end border-secondary">
                                    {t('planning.cost-group-name')}
                                </th>
                                <th colSpan={2} className="planning-col--23 text-center text-uppercase fw-semibold border-end border-secondary">
                                    {t('planning.cost-calculation')}
                                </th>
                                <th colSpan={2} className="planning-col--23 text-center text-uppercase fw-semibold border-end border-secondary">
                                    {t('planning.forecast-completion-with-risk')}
                                </th>
                                <th colSpan={3} className="planning-col--27 text-center text-uppercase fw-semibold border-end border-secondary">
                                    {t('planning.difference-cost-calculation')}
                                </th>
                            </tr>
                            {/* Sub Header Row - Compact */}
                            <tr>
                                <th className="planning-col--12 text-end text-uppercase fw-normal text-light opacity-75">
                                    {t('planning.netto')}
                                </th>
                                <th className="planning-col--11 text-end text-uppercase fw-normal text-light opacity-75">
                                    {t('planning.brutto')}
                                </th>
                                <th className="planning-col--12 text-end text-uppercase fw-normal text-light opacity-75">
                                    {t('planning.netto')}
                                </th>
                                <th className="planning-col--11 text-end text-uppercase fw-normal text-light opacity-75">
                                    {t('planning.brutto')}
                                </th>
                                <th className="planning-col--9 text-end text-uppercase fw-normal text-light opacity-75">
                                    {t('planning.netto')}
                                </th>
                                <th className="planning-col--9 text-end text-uppercase fw-normal text-light opacity-75">
                                    {t('planning.brutto')}
                                </th>
                                <th className="planning-col--9 text-end text-uppercase fw-normal text-light opacity-75">
                                    {t('planning.percent')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {hierarchyData.map(category => (
                                <React.Fragment key={category.id}>
                                    {renderCategoryGroup(category)}
                                </React.Fragment>
                            ))}
                        </tbody>
                        {totals && (
                            <tfoot>
                                <tr className="fw-bold">
                                    <td colSpan={2} className="text-uppercase planning-col--27">
                                        {t('planning.total-summary')}
                                    </td>
                                    <td className="text-end planning-col--12">
                                        {formatCurrencyToLocale(totals.forecastedBudget || 0)}
                                    </td>
                                    <td className="text-end planning-col--11">
                                        {formatCurrencyToLocale(totals.forecastedBudgetGross || 0)}
                                    </td>
                                    <td className="text-end planning-col--12">
                                        <span className="text-success">
                                            {formatCurrencyToLocale(totals.forecastedBudgetWithRisk || 0)}
                                        </span>
                                    </td>
                                    <td className="text-end planning-col--11">
                                        <span className="text-success">
                                            {formatCurrencyToLocale(totals.forecastedBudgetWithRiskGross || 0)}
                                        </span>
                                    </td>
                                    <td className="text-end planning-col--9">
                                        <span className={totals.differenceCostCalculationNet >= 0 ? 'text-success' : 'text-danger'}>
                                            {formatCurrencyToLocale(totals.differenceCostCalculationNet || 0)}
                                        </span>
                                    </td>
                                    <td className="text-end planning-col--9">
                                        <span className={totals.differenceCostCalculationGross >= 0 ? 'text-success' : 'text-danger'}>
                                            {formatCurrencyToLocale(totals.differenceCostCalculationGross || 0)}
                                        </span>
                                    </td>
                                    <td className="text-end planning-col--9">
                                        <span className={totals.differenceCostCalculationPercent >= 0 ? 'text-success' : 'text-danger'}>
                                            {(totals.differenceCostCalculationPercent || 0).toFixed(2)}%
                                        </span>
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </Table>
                </div>
            </Card.Body>
        </Card>
    );
}

export default PlanningHierarchy;
